import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType } from "building-types/views/sheetGeometryTypes";
import { productLog } from "common/utils/logging";
import { numMul } from "common/utils/nominalNumbers";
import type { TaggedPlanGeometry } from "doc/plans/planGeometrySourceTypes";
import { elevationDefaultCategoryStyles } from "doc/plans/planStyleSheets";
import { resolveSheetGeometryStyle } from "doc/plans/primitives/primitiveStyles";
import { normalizeTextBox, planTextGeometryAABB } from "doc/plans/primitives/textPrimitive";
import { sheetGeometryAABB2 } from "doc/plans/sheetGeometryAABB2";
import { aabb2ByPolyLine2s } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { intersectPolyLine2PolyLine2 } from "geom/intersect";
import { line2ClipToAABB } from "geom/line2ClipToAABB";
import { polyLine2AABB2, polyLine2Copy } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { STANDARD_FONT_SIZE } from "pdf/planFonts";
import RBush from "rbush";
import { SVGElementToCollisionWeight } from "svg/SVGElementToCollisionWeight";

// Set to true to enable logging
const LOG_ENABLED = false;

export enum CollisionShapeType {
  AABB = "AABB",
  POLYLINE = "POLYLINE",
}

interface CollisionItem2Base extends AABB2 {
  index: number;
  weight: number;
  blocksLeaderLines: boolean;
}

interface CollisionAABBItem2 extends CollisionItem2Base {
  shapeType: CollisionShapeType.AABB;
  polyline?: undefined;
}

interface CollisionPolyLineItem2 extends CollisionItem2Base {
  shapeType: CollisionShapeType.POLYLINE;
  polyline: PolyLine2;
}

export type CollisionItem2 = CollisionAABBItem2 | CollisionPolyLineItem2;

function polyLine2IntersectsAABB(polyline: PolyLine2, aabb: AABB2): boolean {
  for (let index = 0; index < polyline.length - 2; index += 2) {
    if (
      line2ClipToAABB(polyline[index], polyline[index + 1], polyline[index + 2], polyline[index + 3], aabb) !==
      undefined
    ) {
      return true;
    }
  }
  return false;
}

function polyLine2IntersectsCollisionItem(polyline: PolyLine2, item: CollisionItem2): boolean {
  if (item.shapeType === CollisionShapeType.AABB) {
    return polyLine2IntersectsAABB(polyline, item);
  }
  return intersectPolyLine2PolyLine2(polyline, item.polyline).length > 0;
}

export const SheetGeometryToSVGElement: { [key: string]: string } = {
  [SheetGeometryType.TEXT]: "text",
  [SheetGeometryType.CIRCLE]: "circle",
};

export class CollisionSystem2 {
  private itemCounter: number;
  private readonly rTree: RBush<CollisionItem2>;
  private readonly aabbTree: RBush<CollisionAABBItem2>;
  private readonly leaderLineTree: RBush<CollisionItem2>;
  private readonly roundingFactor = 1000;
  private log: string[] | undefined = LOG_ENABLED ? [] : undefined;

  private roundValue(value: number): number {
    return Math.round(value * this.roundingFactor) / this.roundingFactor;
  }

  private roundAABB2(aabb: AABB2): AABB2 {
    return {
      minX: this.roundValue(aabb.minX),
      minY: this.roundValue(aabb.minY),
      maxX: this.roundValue(aabb.maxX),
      maxY: this.roundValue(aabb.maxY),
    };
  }

  constructor() {
    this.rTree = new RBush<CollisionItem2>();
    this.aabbTree = new RBush<CollisionAABBItem2>();
    this.leaderLineTree = new RBush<CollisionItem2>();
    this.itemCounter = 0;
  }

  private idNew() {
    return (this.itemCounter++).toString();
  }

  insert(item: AABB2, weight = 0, blocksLeaderLines = true): string {
    const id = this.idNew();
    // Round the bbox to the nearest 0.01
    item = this.roundAABB2(item);
    this.log?.push(`insert: ${item.minX}, ${item.minY}, ${item.maxX}, ${item.maxY}, ${weight}`);
    const itemIndexed: CollisionAABBItem2 = {
      minX: item.minX,
      minY: item.minY,
      maxX: item.maxX,
      maxY: item.maxY,
      index: parseInt(id),
      weight,
      blocksLeaderLines,
      shapeType: CollisionShapeType.AABB,
    };
    this.rTree.insert(itemIndexed);
    this.aabbTree.insert(itemIndexed);
    if (blocksLeaderLines) {
      this.leaderLineTree.insert(itemIndexed);
    }
    return id;
  }

  insertMany(items: AABB2[], weight = 0, blocksLeaderLines = true) {
    const id = this.idNew();
    const itemsIndexed: CollisionAABBItem2[] = [];
    for (const item of items) {
      itemsIndexed.push({
        minX: item.minX,
        minY: item.minY,
        maxX: item.maxX,
        maxY: item.maxY,
        index: parseInt(id),
        weight,
        blocksLeaderLines,
        shapeType: CollisionShapeType.AABB,
      });
    }
    this.rTree.load(itemsIndexed);
    this.aabbTree.load(itemsIndexed);
    if (blocksLeaderLines) {
      this.leaderLineTree.load(itemsIndexed);
    }
    return id;
  }

  insertPolyLine2(item: PolyLine2, weight = 0, blocksLeaderLines = true) {
    const id = this.idNew();
    const base = polyLine2AABB2(item);
    const itemIndexed: CollisionPolyLineItem2 = {
      minX: base.minX,
      minY: base.minY,
      maxX: base.maxX,
      maxY: base.maxY,
      index: parseInt(id),
      weight,
      blocksLeaderLines,
      shapeType: CollisionShapeType.POLYLINE,
      polyline: polyLine2Copy(item),
    };
    this.rTree.insert(itemIndexed);
    if (blocksLeaderLines) {
      this.leaderLineTree.insert(itemIndexed);
    }
    return id;
  }

  insertManyPolyLine2(items: PolyLine2[], weight = 0, blocksLeaderLines = true) {
    const id = this.idNew();
    const itemsIndexed: CollisionPolyLineItem2[] = [];
    for (const item of items) {
      const base = polyLine2AABB2(item);
      itemsIndexed.push({
        minX: base.minX,
        minY: base.minY,
        maxX: base.maxX,
        maxY: base.maxY,
        index: parseInt(id),
        weight,
        blocksLeaderLines,
        shapeType: CollisionShapeType.POLYLINE,
        polyline: polyLine2Copy(item),
      });
    }
    this.rTree.load(itemsIndexed);
    if (blocksLeaderLines) {
      this.leaderLineTree.load(itemsIndexed);
    }
    return id;
  }

  collides(search: AABB2): boolean {
    if (this.aabbTree.collides(search)) {
      return true;
    }
    return this.rTree.search(search).some((item) => {
      return item.shapeType === CollisionShapeType.POLYLINE && polyLine2IntersectsAABB(item.polyline, search);
    });
  }

  search(searchItem: AABB2): number[] {
    return this.searchItems(searchItem).map((x) => x.index);
  }

  /** Tests a polyline using the R-tree for broad phase and retained collider shapes for fine rejection. */
  collidesPolyLine2(search: PolyLine2): boolean {
    return this.rTree.search(polyLine2AABB2(search)).some((item) => polyLine2IntersectsCollisionItem(search, item));
  }

  /** Returns precise collider matches for a polyline query. */
  searchPolyLine2(search: PolyLine2): number[] {
    return this.searchPolyLine2Items(search).map((x) => x.index);
  }

  /** Tests a leader line against collision shapes explicitly marked as leader blockers. */
  collidesLeaderLine(search: PolyLine2): boolean {
    return this.leaderLineTree
      .search(polyLine2AABB2(search))
      .some((item) => polyLine2IntersectsCollisionItem(search, item));
  }

  /** Returns precise collider matches that are explicitly marked as leader blockers. */
  searchLeaderLine(search: PolyLine2): number[] {
    return this.searchLeaderLineItems(search).map((x) => x.index);
  }

  score(searchItem: AABB2): number {
    return this.rTree.search(searchItem).reduce((prev, cur) => {
      return prev + cur.weight;
    }, 0);
  }

  scoreSheetGeometries(geometries: TaggedPlanGeometry[], svgTextScale?: number): number {
    // Print the log if enabled, on the first scoreSheetGeometries call
    if (LOG_ENABLED) {
      productLog(this.getLog());
    }
    let score = 0;
    for (const searchItem of geometries) {
      const searchItemWeight = SVGElementToCollisionWeight[SheetGeometryToSVGElement[searchItem.type] ?? "default"] / 2;
      let aabb = elevationSheetGeometryAABB2(searchItem, svgTextScale ?? 1);
      if (!aabb) {
        continue;
      }
      aabb = this.roundAABB2(aabb);
      const searchItemScore = this.rTree.search(aabb).reduce((prev, cur) => prev + cur.weight, 0);
      if (searchItemScore > 0) {
        score += searchItemScore + searchItemWeight;
      }
    }

    return score;
  }

  getLog(): string {
    if (!this.log) {
      return "";
    }
    const log = this.log;
    this.log = [];
    return log.join("\n");
  }

  all(): CollisionItem2[] {
    return this.rTree.all();
  }

  private searchItems(searchItem: AABB2): CollisionItem2[] {
    return this.rTree.search(searchItem).filter((item) => {
      if (item.shapeType === CollisionShapeType.AABB) {
        return true;
      }
      return polyLine2IntersectsAABB(item.polyline, searchItem);
    });
  }

  private searchPolyLine2Items(searchItem: PolyLine2): CollisionItem2[] {
    return this.rTree
      .search(polyLine2AABB2(searchItem))
      .filter((item) => polyLine2IntersectsCollisionItem(searchItem, item));
  }

  private searchLeaderLineItems(searchItem: PolyLine2): CollisionItem2[] {
    return this.leaderLineTree
      .search(polyLine2AABB2(searchItem))
      .filter((item) => polyLine2IntersectsCollisionItem(searchItem, item));
  }
}

export function sheetGeometryCollisionAABBs(geometry: SheetGeometry): AABB2[] {
  switch (geometry.type) {
    case SheetGeometryType.POLYLINE: {
      if (geometry.oneObstacle) {
        const aabb = sheetGeometryAABB2(geometry);
        return aabb ? [aabb] : [];
      }

      return geometry.paths.map((path) => polyLine2AABB2(path));
    }
    case SheetGeometryType.POLYGON: {
      return geometry.paths.map((paths) => aabb2ByPolyLine2s(paths)).filter((aabb): aabb is AABB2 => aabb !== null);
    }
    default: {
      const aabb = sheetGeometryAABB2(geometry);
      return aabb ? [aabb] : [];
    }
  }
}

export function elevationSheetGeometryAABB2(geometry: TaggedPlanGeometry, textScalingFactor: number): AABB2 | null {
  // resolve the geometry style using the default styles for the category to maintain material tag placement results
  // WARNING! THIS IS A HACK AND DOES NOT TAKE INTO ACCOUNT THE VIEW SPECIFIC STYLES AND EFFECTS
  // TODO: remove this once material tag placement includes view specific styles and effects
  const styledGeometry = resolveSheetGeometryStyle(geometry, elevationDefaultCategoryStyles[geometry.category]);

  const bbox: AABB2 | null =
    styledGeometry.type === SheetGeometryType.TEXT
      ? planTextGeometryAABB({
          ...normalizeTextBox(styledGeometry),
          rotation: -(styledGeometry.rotation ?? 0),
          fontSize: numMul(styledGeometry.fontSize ?? STANDARD_FONT_SIZE, textScalingFactor),
        })
      : sheetGeometryAABB2(styledGeometry);
  if (!bbox) {
    return null;
  }
  return bbox;
}
